/**
 * Per-passage-set START screen, shown BEFORE each ELA passage set —
 * the counterpart to the end-of-passage ``PassageInterstitial``.
 * Mirrors the official TestNav frame:
 *
 *   ELA – Passage Set 3 of 7
 *   6 Questions
 *   [ Start ]
 *
 * Markup/classes reuse the existing Test Start card
 * (``digitalSHSATModule__startContainer`` etc. from Module/index.css)
 * so no new styling is introduced.
 *
 * Presentational only — used by BOTH the legacy locked flow
 * (Module/index.tsx) and the adaptive flow (AdaptiveModule). The
 * set index/count and question count are optional because the adaptive
 * serve payload doesn't expose the module's full passage-group
 * structure; when absent the header falls back to a plain
 * "SECTION – Passage Set" title and the count row is omitted.
 *
 * Also reused for the ELA "Stand alone items" (Revising/Editing Part B)
 * block: pass ``label="Stand alone items"`` to override the
 * "Passage Set X of N" sub-heading.
 */

import React from 'react';
import { sectionDisplayLabel } from './sectionOrder';

export function PassageSetStart(props: {
  sectionName: string | null;
  setIndex?: number | null;
  setCount?: number | null;
  questionCount?: number | null;
  /** Overrides the "Passage Set X of N" sub-heading (e.g. "Stand alone
   * items"). When omitted, the passage-set label is derived from
   * setIndex/setCount. */
  label?: string | null;
  onStart: () => void;
}): JSX.Element {
  const { sectionName, setIndex, setCount, questionCount, label, onStart } = props;
  const setLabel = label
    ?? (setIndex != null && setCount != null
      ? `Passage Set ${setIndex} of ${setCount}`
      : 'Passage Set');
  return (
    <div className="digitalSHSATModule__startContainer">
      <div className="digitalSHSATModule__startInnerContainer">
        <div className="digitalSHSATModule__startInnerTop">
          <div className="digitalSHSATModule__startInnerTopHeader">
            {sectionDisplayLabel(sectionName) || 'ELA'} – {setLabel}
          </div>
          {questionCount != null && (
            <div className="digitalSHSATModule__startInnerTopInfoBox">
              <div className="digitalSHSATModule__startInnerTopInfo">
                {questionCount} {questionCount === 1 ? 'Question' : 'Questions'}
              </div>
            </div>
          )}
        </div>
        <div className="digitalSHSATModule__startInnerBottom">
          <div
            className="digitalSHSATModule__startInnerBottomButton"
            onClick={onStart}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onStart(); }}
          >
            Start
          </div>
        </div>
      </div>
    </div>
  );
}

export default PassageSetStart;
